import { customColors, customColorsRaw } from '@apollo/orbit';
import _ from 'lodash';
import { useMemo } from 'react';

import { ScalarTimeSeriesData } from 'src/components/charts/helpers/dataTypes';
import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import { zeroPadTimeSeriesData } from 'src/components/charts/helpers/zeroPadTimeSeriesData';
import { LineChartStackConfig } from 'src/components/charts/LineChart';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { FetchLatencyOverTimeResultFragment } from 'src/lib/graphqlTypes/types';

export type FetchLatencyMetricType = 'p50' | 'p90' | 'p95' | 'p99';
export type FetchLatencyOverTimeResultRecord =
  FetchLatencyOverTimeResultFragment['records'][0];

function getLabel(metricType: FetchLatencyMetricType) {
  switch (metricType) {
    case 'p50':
    case 'p90':
    case 'p95':
    case 'p99':
      return 'Fetch Time';
    default:
      assertUnreachable(metricType);
  }
}

function getUnit(metricType: FetchLatencyMetricType) {
  switch (metricType) {
    case 'p50':
    case 'p90':
    case 'p95':
    case 'p99':
      return undefined;
    default:
      assertUnreachable(metricType);
  }
}

export function useFetchLatencyChartStacks({
  result,
  metricType,
  resolution,
}: {
  result?: FetchLatencyOverTimeResultFragment;
  metricType: FetchLatencyMetricType;
  resolution: GraphQLTypes.Resolution;
}) {
  const getValue = useMemo(() => {
    switch (metricType) {
      case 'p50':
        return (record: FetchLatencyOverTimeResultRecord) =>
          record.data.fetchLatencyHistogram?.p50 ?? 0;
      case 'p90':
        return (record: FetchLatencyOverTimeResultRecord) =>
          record.data.fetchLatencyHistogram?.p90 ?? 0;
      case 'p95':
        return (record: FetchLatencyOverTimeResultRecord) =>
          record.data.fetchLatencyHistogram?.p95 ?? 0;
      case 'p99':
        return (record: FetchLatencyOverTimeResultRecord) =>
          record.data.fetchLatencyHistogram?.p99 ?? 0;
      default:
        return assertUnreachable(metricType);
    }
  }, [metricType]);
  const normalizedData: ScalarTimeSeriesData<number>[] = useMemo(
    () =>
      result
        ? zeroPadTimeSeriesData({
            data: result.records,
            timeFrom: new Date(result.roundedDownFrom),
            timeTo: new Date(result.roundedUpTo),
            resolution,
            getValue,
            getZeroFillValue: () => 0,
          })
        : [],
    [getValue, resolution, result],
  );

  const stacks = useMemo(
    () => [
      {
        colorGradientTop: customColors.bg.beta,
        colorGradientBottom: `rgb(${customColorsRaw.bg.beta} / 0)`,
        primaryColor: customColors.border.beta,
        cursorColor: customColors.brand.accent,
        label: getLabel(metricType),
        unit: getUnit(metricType),
        data: normalizedData,
        hasData: () => true,
        getValue: (datapoint: ScalarTimeSeriesData<number>) => datapoint,
      },
    ],
    [metricType, normalizedData],
  );

  const fetchErrorsChartStackInfo = useStackInfo<
    ScalarTimeSeriesData<number>,
    ScalarTimeSeriesData<number>,
    LineChartStackConfig<ScalarTimeSeriesData<number>>
  >(stacks);

  return {
    fetchErrorsChartStackInfo,
  };
}
